import { useEffect, useState } from "react";
import { api, errMsg, formatDate, uploadImage } from "@/lib/api";
import AuthImage from "@/components/AuthImage";
import { useAuth } from "@/context/AuthContext";
import { Camera, Loader2, Plus, X } from "lucide-react";

const ZONES = ["Pelaminan", "Welcome Gate", "Photobooth", "Lorong / Aisle", "Meja Tamu", "Panggung", "Lainnya"];
const STATUS = ["Belum Mulai", "Dikerjakan", "Selesai"];
const STATUS_TONE = { "Belum Mulai": "bg-[#F2EEE7] text-[#536263]", "Dikerjakan": "bg-amber-100 text-amber-900", "Selesai": "bg-emerald-100 text-emerald-800" };
const EMPTY = { zone: "Pelaminan", title: "", instructions: "", assignee_id: "", deadline: "", photos: [] };

export function PhotoGrid({ photos, onOpen }) {
  if (!photos?.length) return null;
  return (
    <div className="flex gap-2 overflow-x-auto no-scrollbar mt-2">
      {photos.map(p => (
        <button key={p} type="button" onClick={() => onOpen(p)} className="shrink-0">
          <AuthImage path={p} alt="Referensi" className="w-20 h-20 object-cover rounded-lg border border-[#E2DDD3]" />
        </button>
      ))}
    </div>
  );
}

function SpkForm({ projectId, onDone, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [people, setPeople] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/users/assignable").then(r => setPeople(r.data)); }, []);

  async function addPhotos(e) {
    setBusy(true);
    try {
      const paths = await Promise.all([...e.target.files].map(uploadImage));
      setForm(f => ({ ...f, photos: [...f.photos, ...paths] }));
    } catch (ex) { setErr(errMsg(ex)); }
    setBusy(false);
  }

  async function submit(e) {
    e.preventDefault();
    try {
      await api.post("/spk", { ...form, project_id: projectId, assignee_id: form.assignee_id || null });
      onDone();
    } catch (ex) { setErr(errMsg(ex)); }
  }

  const input = "w-full border border-[#E2DDD3] rounded-lg px-3 h-11";
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()} data-testid="spk-form"
        className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-5 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-serif-display text-xl font-bold">SPK Zona Baru</h3>
          <button type="button" onClick={onClose}><X /></button>
        </div>
        <select value={form.zone} onChange={e => setForm({ ...form, zone: e.target.value })} className={input} data-testid="spk-zone-select">
          {ZONES.map(z => <option key={z}>{z}</option>)}
        </select>
        <input required placeholder="Judul pekerjaan" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className={input} data-testid="spk-title-input" />
        <textarea placeholder="Instruksi detail (ukuran, warna, material...)" value={form.instructions} onChange={e => setForm({ ...form, instructions: e.target.value })}
          className="w-full border border-[#E2DDD3] rounded-lg px-3 py-2 min-h-[90px]" data-testid="spk-instructions-input" />
        <div className="grid grid-cols-2 gap-3">
          <select value={form.assignee_id} onChange={e => setForm({ ...form, assignee_id: e.target.value })} className="border border-[#E2DDD3] rounded-lg px-3 h-11" data-testid="spk-assignee-select">
            <option value="">Penanggung jawab</option>
            {people.map(p => <option key={p.id} value={p.id}>{p.name} ({p.role})</option>)}
          </select>
          <input type="date" value={form.deadline} onChange={e => setForm({ ...form, deadline: e.target.value })} className="border border-[#E2DDD3] rounded-lg px-3 h-11" data-testid="spk-deadline-input" />
        </div>
        <label className="flex items-center gap-3 border-2 border-dashed border-[#E2DDD3] rounded-lg p-3 cursor-pointer">
          {busy ? <Loader2 className="animate-spin text-[#B87333]" size={20} /> : <Camera size={20} className="text-[#B87333]" />}
          <span className="text-sm text-[#536263]">{form.photos.length ? `${form.photos.length} foto referensi` : "Upload foto referensi"}</span>
          <input type="file" accept="image/*" multiple onChange={addPhotos} className="hidden" data-testid="spk-photo-upload" />
        </label>
        <PhotoGrid photos={form.photos} onOpen={() => {}} />
        {err && <div className="text-sm text-red-700">{err}</div>}
        <button disabled={busy} className="btn-primary w-full" data-testid="spk-submit">Simpan SPK</button>
      </form>
    </div>
  );
}

const REVIEW_TONE = { "Menunggu Cek": "bg-amber-100 text-amber-900", "Disetujui": "bg-emerald-100 text-emerald-800", "Revisi": "bg-red-100 text-red-800" };

export function SpkCard({ s, onStatus, onOpen, showProject, onChanged }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const canReview = ["Owner", "Produksi"].includes(user?.role);
  const needsPhoto = !s.result_photos?.length || s.review_status === "Revisi";

  async function submitDone(e) {
    const files = [...e.target.files];
    e.target.value = "";
    if (!files.length) return;
    setBusy(true); setErr("");
    try {
      const paths = await Promise.all(files.map(uploadImage));
      const result_photos = s.review_status === "Revisi" ? paths : [...(s.result_photos || []), ...paths];
      await api.patch(`/spk/${s.id}`, { status: "Selesai", result_photos });
      onChanged?.();
    } catch (ex) { setErr(errMsg(ex)); }
    setBusy(false);
  }

  async function review(approve) {
    const note = approve ? "" : window.prompt("Catatan revisi untuk tim:");
    if (!approve && !note) return;
    try { await api.post(`/spk/${s.id}/review`, { approve, note }); onChanged?.(); }
    catch (ex) { setErr(errMsg(ex)); }
  }

  return (
    <div className="card-editorial p-4" data-testid={`spk-card-${s.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-mono-num text-[#B87333]">{s.number}</span>
        <span className={`chip ${STATUS_TONE[s.status]}`}>{s.status}</span>
      </div>
      <h4 className="font-serif-display text-lg font-semibold mt-1">{s.title}</h4>
      <div className="text-xs text-[#8A999A]">
        {showProject && <>{s.project_name} · </>}PJ: {s.assignee_name}{s.deadline && <> · Deadline {formatDate(s.deadline)}</>}
      </div>
      {s.instructions && <p className="text-sm text-[#536263] mt-2 whitespace-pre-line">{s.instructions}</p>}
      {s.photos?.length > 0 && <div className="text-[10px] uppercase tracking-wider text-[#8A999A] mt-2">Referensi</div>}
      <PhotoGrid photos={s.photos} onOpen={onOpen} />
      {s.result_photos?.length > 0 && (
        <div className="mt-3 p-3 bg-[#FAF7F2] rounded-lg" data-testid={`spk-result-${s.id}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="text-[10px] uppercase tracking-wider text-[#B87333]">Foto Hasil{s.submitted_by ? ` · ${s.submitted_by}` : ""}</div>
            {s.review_status && <span className={`chip ${REVIEW_TONE[s.review_status]}`} data-testid={`spk-review-status-${s.id}`}>{s.review_status}</span>}
          </div>
          <PhotoGrid photos={s.result_photos} onOpen={onOpen} />
          {s.review_note && <p className="text-xs text-red-700 mt-2">Revisi: {s.review_note}</p>}
          {canReview && s.review_status === "Menunggu Cek" && (
            <div className="flex gap-2 mt-3">
              <button onClick={() => review(true)} className="chip flex-1 justify-center min-h-[36px] bg-emerald-100 text-emerald-800" data-testid={`spk-approve-${s.id}`}>Setujui</button>
              <button onClick={() => review(false)} className="chip flex-1 justify-center min-h-[36px] bg-red-100 text-red-800" data-testid={`spk-revise-${s.id}`}>Minta Revisi</button>
            </div>
          )}
        </div>
      )}
      <div className="flex gap-2 mt-3">
        {STATUS.filter(st => st !== "Selesai").map(st => (
          <button key={st} onClick={() => onStatus(s, st)} data-testid={`spk-status-${s.id}-${st.split(" ")[0].toLowerCase()}`}
            className={`chip flex-1 justify-center min-h-[36px] ${s.status === st ? "bg-[#0F5257] text-white" : "bg-[#F2EEE7] text-[#536263]"}`}>{st}</button>
        ))}
        {s.status === "Selesai" && !needsPhoto ? (
          <span className="chip flex-1 justify-center min-h-[36px] bg-[#0F5257] text-white" data-testid={`spk-status-${s.id}-selesai`}>Selesai</span>
        ) : (
          <label className={`chip flex-1 justify-center min-h-[36px] cursor-pointer bg-[#F7EFE8] text-[#B87333] ${busy ? "opacity-60 pointer-events-none" : ""}`} data-testid={`spk-status-${s.id}-selesai`}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}Selesai
            <input type="file" accept="image/*" multiple capture="environment" onChange={submitDone} className="hidden" data-testid={`spk-result-upload-${s.id}`} />
          </label>
        )}
      </div>
      <p className="text-[10px] text-[#8A999A] mt-1">Tandai Selesai dengan foto hasil pekerjaan untuk dicek tim Produksi.</p>
      {err && <div className="text-xs text-red-700 mt-1">{err}</div>}
    </div>
  );
}

export function PhotoViewer({ path, onClose }) {
  if (!path) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="photo-viewer">
      <AuthImage path={path} alt="Referensi" className="max-h-full max-w-full rounded-lg" />
    </div>
  );
}

export default function SpkPanel({ projectId }) {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const canCreate = ["Owner", "Produksi"].includes(user?.role);

  async function load() {
    const { data } = await api.get(`/projects/${projectId}/spk`);
    setList(data);
  }
  useEffect(() => { load(); }, [projectId]);

  async function setStatus(s, status) {
    await api.patch(`/spk/${s.id}`, { status });
    await load();
  }

  const zones = [...new Set(list.map(s => s.zone))];
  return (
    <div className="space-y-4" data-testid="spk-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-serif-display text-lg font-semibold">SPK per Zona</h3>
        {canCreate && <button onClick={() => setOpen(true)} className="btn-primary" data-testid="spk-add-btn"><Plus size={16} />SPK Baru</button>}
      </div>
      {zones.map(z => (
        <div key={z}>
          <div className="text-xs uppercase tracking-wider text-[#B87333] mb-2">{z}</div>
          <div className="grid md:grid-cols-2 gap-3">
            {list.filter(s => s.zone === z).map(s => <SpkCard key={s.id} s={s} onStatus={setStatus} onOpen={setViewing} onChanged={load} />)}
          </div>
        </div>
      ))}
      {list.length === 0 && <p className="text-sm text-[#8A999A]">Belum ada SPK</p>}
      {open && <SpkForm projectId={projectId} onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />}
      <PhotoViewer path={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}
